import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ResponsiveSection from "@/components/layout/ResponsiveSection";
import ExpertSection from "@/components/ExpertSection";

import { Laptop, Zap, Shield, TrendingUp, Check, AlertCircle, FileText, Home, Building2, HeartPulse } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";

const Elektronikversicherung = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="relative z-10">
        <Navigation />
      
      {/* Breadcrumbs */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/weitere-versicherungen">Weitere Versicherungen</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Elektronikversicherung</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      {/* Hero Section */}
      <ResponsiveSection spacing="lg">
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-accent text-background">Ergänzend</Badge>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 break-words">
            Elektronikversicherung für Arztpraxen
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
            Umfassender Schutz für Ihre medizinische und IT-Ausstattung
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <Card className="card-glass text-center">
            <CardContent className="pt-6">
              <Laptop className="w-10 h-10 sm:w-12 sm:h-12 text-accent mx-auto mb-4" />
              <div className="text-lg sm:text-xl md:text-2xl font-bold mb-1">50-200k €</div>
              <div className="text-xs sm:text-sm text-muted-foreground">Elektronikwert</div>
            </CardContent>
          </Card>
          <Card className="card-glass text-center">
            <CardContent className="pt-6">
              <Zap className="w-10 h-10 sm:w-12 sm:h-12 text-accent mx-auto mb-4" />
              <div className="text-lg sm:text-xl md:text-2xl font-bold mb-1">70%</div>
              <div className="text-xs sm:text-sm text-muted-foreground">Ausfälle</div>
            </CardContent>
          </Card>
          <Card className="card-glass text-center">
            <CardContent className="pt-6">
              <Shield className="w-10 h-10 sm:w-12 sm:h-12 text-accent mx-auto mb-4" />
              <div className="text-lg sm:text-xl md:text-2xl font-bold mb-1">Neuwert</div>
              <div className="text-xs sm:text-sm text-muted-foreground">Entschädigung</div>
            </CardContent>
          </Card>
          <Card className="card-glass text-center">
            <CardContent className="pt-6">
              <TrendingUp className="w-10 h-10 sm:w-12 sm:h-12 text-accent mx-auto mb-4" />
              <div className="text-lg sm:text-xl md:text-2xl font-bold mb-1">ab 300 €</div>
              <div className="text-xs sm:text-sm text-muted-foreground">Jahresbeitrag</div>
            </CardContent>
          </Card>
        </div>
      </ResponsiveSection>

      {/* Leistungsübersicht */}
      <ResponsiveSection spacing="lg">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 text-center text-foreground break-words">
          Leistungsübersicht
        </h2>
        <p className="text-sm text-muted-foreground mb-12 text-center">
          Umfassender Schutz für Ihre gesamte Elektronik
        </p>

        <div className="glas-cards grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 xl:gap-16">
          {/* Versicherte Geräte */}
          <div className="relative pb-6 md:pb-0">
            <div className="relative z-10">
              <div className="flex flex-col lg:flex-row items-center gap-3 mb-3 lg:mb-2">
                <Laptop className="w-8 h-8 lg:w-5 lg:h-5 text-accent" />
                <h3 className="text-lg sm:text-xl font-bold text-foreground text-center lg:text-left">
                  Versicherte Geräte
                </h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4 lg:mb-6 text-center lg:text-left">
                Schutz für Ihre gesamte Elektronik
              </p>
              
              <div className="list-container">
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">PVS und Server</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Medizinische Großgeräte (MRT, CT, Ultraschall)</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Labor- und Diagnostikgeräte</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Computer, Laptops, Tablets, Bildschirme</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Drucker, Scanner, Kopierer</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Telefonanlagen und Netzwerktechnik</span>
                </div>
              </div>
            </div>
          </div>

          {/* Versicherte Schäden */}
          <div className="relative">
            <div className="relative z-10">
              <div className="flex flex-col lg:flex-row items-center gap-3 mb-3 lg:mb-2">
                <AlertCircle className="w-8 h-8 lg:w-5 lg:h-5 text-accent" />
                <h3 className="text-lg sm:text-xl font-bold text-foreground text-center lg:text-left">
                  Versicherte Schäden
                </h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4 lg:mb-6 text-center lg:text-left">
                Umfassende Schadenabdeckung
              </p>
              
              <div className="list-container">
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Bedienungsfehler</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Überspannung durch Blitzschlag</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Kurzschluss und elektrische Defekte</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Brand und Explosion</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Wasserschäden</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Diebstahl und Einbruch</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ResponsiveSection>

      {/* Besondere Vorteile */}
      <ResponsiveSection spacing="lg">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-8 text-center text-foreground break-words">
          Besondere Vorteile
        </h2>

        <div className="glas-cards">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start gap-3">
              <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Neuwertentschädigung</h3>
                <p className="text-xs sm:text-sm text-foreground/70">Ohne Zeitwertabzug - voller Neuwert wird erstattet</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Schnelle Regulierung</h3>
                <p className="text-xs sm:text-sm text-foreground/70">Oft innerhalb 48h nach Schadenmeldung</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Weltweiter Schutz</h3>
                <p className="text-xs sm:text-sm text-foreground/70">Bei mobilen Geräten weltweit versichert</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Datenrettung inklusive</h3>
                <p className="text-xs sm:text-sm text-foreground/70">Einschluss von Software und Datenrettung</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Ersatzgeräte</h3>
                <p className="text-xs sm:text-sm text-foreground/70">Ersatzgeräte während Reparatur verfügbar</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">24/7 Support</h3>
                <p className="text-xs sm:text-sm text-foreground/70">Technischer Support rund um die Uhr inkludiert</p>
              </div>
            </div>
          </div>
        </div>
      </ResponsiveSection>

      {/* Für wen geeignet */}
      <ResponsiveSection spacing="lg">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-8 text-center break-words">
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary via-accent to-primary">
            Für wen ist diese Versicherung geeignet?
          </span>
        </h2>
        <p className="text-sm text-muted-foreground mb-12 text-center">
          Besonders wichtig bei hochwertiger Ausstattung
        </p>

        <div className="grid md:grid-cols-3 gap-6">
          <Card className="card-glass">
            <CardContent className="pt-6">
              <HeartPulse className="w-10 h-10 sm:w-12 sm:h-12 text-accent mx-auto mb-4" />
              <h3 className="font-bold text-foreground mb-3 text-center text-sm sm:text-base">Radiologische Praxen</h3>
              <ul className="space-y-2 text-xs sm:text-sm text-foreground/70">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Hoher Wert medizinischer Großgeräte</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>MRT, CT, Röntgengeräte</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Besonders ausfallgefährdet</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="card-glass">
            <CardContent className="pt-6">
              <Building2 className="w-10 h-10 sm:w-12 sm:h-12 text-accent mx-auto mb-4" />
              <h3 className="font-bold text-foreground mb-3 text-center text-sm sm:text-base">Labore & Diagnostikzentren</h3>
              <ul className="space-y-2 text-xs sm:text-sm text-foreground/70">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Empfindliche Analyse- und Messgeräte</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Teure Laborausstattung</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Hohe Ersatzbeschaffungskosten</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="card-glass">
            <CardContent className="pt-6">
              <Laptop className="w-10 h-10 sm:w-12 sm:h-12 text-accent mx-auto mb-4" />
              <h3 className="font-bold text-foreground mb-3 text-center text-sm sm:text-base">Digital ausgestattete Praxen</h3>
              <ul className="space-y-2 text-xs sm:text-sm text-foreground/70">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>PVS, Server, IT-Ausstattung</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Umfangreiche Netzwerktechnik</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>Digitale Patientenakten</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </ResponsiveSection>

      {/* Schadensbeispiele */}
      <ResponsiveSection spacing="lg">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 text-center text-foreground break-words">
          Echte Schadensbeispiele
        </h2>
        <p className="text-sm text-muted-foreground mb-12 text-center">
          Reale Fälle zeigen die Notwendigkeit
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="card-glass">
            <CardContent className="pt-6">
              <Badge className="mb-3 bg-accent text-background">Schadenhöhe: 25.000 €</Badge>
              <h3 className="font-bold text-foreground mb-2 text-sm sm:text-base">Server durch Blitzschlag</h3>
              <p className="text-xs sm:text-sm text-foreground/70 mb-3">
                PVS komplett ausgefallen. Datenrettung notwendig, Notfall-IT musste bereitgestellt werden.
              </p>
              <div className="flex justify-between text-xs sm:text-sm">
                <span className="text-foreground/60">Deckung:</span>
                <span className="text-accent font-semibold">Neuwert + Datenrettung</span>
              </div>
            </CardContent>
          </Card>

          <Card className="card-glass">
            <CardContent className="pt-6">
              <Badge className="mb-3 bg-accent text-background">Schadenhöhe: 45.000 €</Badge>
              <h3 className="font-bold text-foreground mb-2 text-sm sm:text-base">Ultraschallgerät-Defekt</h3>
              <p className="text-xs sm:text-sm text-foreground/70 mb-3">
                Defekt durch Bedienungsfehler. Vollständige Neuanschaffung notwendig.
              </p>
              <div className="flex justify-between text-xs sm:text-sm">
                <span className="text-foreground/60">Deckung:</span>
                <span className="text-accent font-semibold">Vollständige Neuanschaffung</span>
              </div>
            </CardContent>
          </Card>

          <Card className="card-glass">
            <CardContent className="pt-6">
              <Badge className="mb-3 bg-accent text-background">Schadenhöhe: 18.000 €</Badge>
              <h3 className="font-bold text-foreground mb-2 text-sm sm:text-base">Wasserschaden IT</h3>
              <p className="text-xs sm:text-sm text-foreground/70 mb-3">
                Rohrbruch zerstörte komplette IT-Infrastruktur. Server, PCs, Netzwerk mussten ersetzt werden.
              </p>
              <div className="flex justify-between text-xs sm:text-sm">
                <span className="text-foreground/60">Deckung:</span>
                <span className="text-accent font-semibold">Komplett ersetzt</span>
              </div>
            </CardContent>
          </Card>

          <Card className="card-glass">
            <CardContent className="pt-6">
              <Badge className="mb-3 bg-accent text-background">Schadenhöhe: 8.500 €</Badge>
              <h3 className="font-bold text-foreground mb-2 text-sm sm:text-base">Kurzschluss Praxis-PC</h3>
              <p className="text-xs sm:text-sm text-foreground/70 mb-3">
                Elektrischer Defekt zerstörte mehrere Arbeitsplatz-PCs samt Monitoren und Peripherie.
              </p>
              <div className="flex justify-between text-xs sm:text-sm">
                <span className="text-foreground/60">Deckung:</span>
                <span className="text-accent font-semibold">Vollständig übernommen</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </ResponsiveSection>

      {/* CTA Section */}
      <ResponsiveSection spacing="lg">
        <div className="text-center">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 text-foreground">
            Jetzt persönliche Beratung vereinbaren
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground mb-8 max-w-2xl mx-auto">
            Als spezialisierter Versicherungsmakler für Ärzte unterstütze ich Sie bei der Auswahl der passenden Elektronikversicherung.
          </p>
          <Button asChild size="lg" className="bg-accent hover:bg-accent/90">
            <Link to="/termin-buchen">Beratungstermin buchen</Link>
          </Button>
        </div>
      </ResponsiveSection>

      <ExpertSection />
      <Footer />
      </div>
    </div>
  );
};

export default Elektronikversicherung;
